import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Select, SelectOption } from '~components/select';
import { useStoreActions, useStoreState } from '~store/hooks';
import { INAPP_TRIGGERS, isInAppPage } from '../../helpers';
import { InAppSection, LabelDiv, ClearIcon } from './styles';
import { TextField } from '~components/inputs';
import { DateTimePicker } from '~components/inputs/date-time-picker';

const InAppTriggerOptions: SelectOption<INAPP_TRIGGERS>[] = [
  {
    name: 'On Open',
    value: INAPP_TRIGGERS.APP_OPEN
  },
  {
    name: 'On Custom Event (SDK)',
    value: INAPP_TRIGGERS.CUSTOM_TRIGGER
  }
];

export const InAppInputs: React.FC = () => {
  const router = useRouteMatch();
  const { hideInappExpiresAtField } = useFlags();
  const { notification } = useStoreState((state) => state.createNotification);
  const [trigger, setTrigger] = React.useState<INAPP_TRIGGERS>(notification?.inApp?.trigger);
  const { setInApp } = useStoreActions((state) => state.createNotification);

  const handleTriggerChange = ([option]: SelectOption<INAPP_TRIGGERS>[]) => {
    setTrigger(option.value);
    setInApp({
      ...notification.inApp,
      customEvent: option.value !== INAPP_TRIGGERS.APP_OPEN ? notification?.inApp?.customEvent : undefined,
      trigger: option.value
    });
  };
  const handleCustomEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInApp({
      ...notification.inApp,
      customEvent: event.target.value
    });
  };
  const handleExpiresAtChange = (expiresAt: Date) => {
    setInApp({
      ...notification.inApp,
      expiresAt
    });
  };

  React.useEffect(() => {
    setTrigger(notification.inApp?.trigger);
  }, [notification]);

  return useMemo(
    () =>
      isInAppPage(router) && (
        <InAppSection data-testid="inAppTriggerSection">
          <LabelDiv>
            <label htmlFor="inAppTrigger">
              Trigger
              <span>Event to trigger the in-app notification</span>
            </label>
          </LabelDiv>
          <Select
            id="inAppTrigger"
            testId="inAppTriggerSelect"
            value={InAppTriggerOptions.filter((option) => option.value === trigger)}
            options={InAppTriggerOptions}
            onChange={handleTriggerChange}
            drop="down"
            placeholder="Select Trigger"
            inValid={false}
          />
          <LabelDiv>
            <label htmlFor="inAppCustomEvent">
              Custom Event (SDK)
              <span>Event to trigger the in-app notification when using a custom trigger</span>
            </label>
          </LabelDiv>
          <TextField
            name="InAppCustomEventTextField"
            value={notification?.inApp?.customEvent}
            onChange={handleCustomEventChange}
            disabled={notification?.inApp?.trigger !== INAPP_TRIGGERS.CUSTOM_TRIGGER}
          />
          {!hideInappExpiresAtField && (
            <>
              <LabelDiv>
                <label htmlFor="schedulingSendAt">
                  Expires At
                  <span>
                    Determines for how long the in-app message is available to be surfaced post-pushing of the message
                  </span>
                </label>
              </LabelDiv>
              <DateTimePicker
                id="inappExpiresAt"
                value={notification?.inApp?.expiresAt && new Date(notification.inApp?.expiresAt)}
                onChange={handleExpiresAtChange}
                clearIcon={<ClearIcon />}
              />
            </>
          )}
        </InAppSection>
      ),
    [notification?.inApp, notification?.inApp?.trigger, trigger]
  );
};
